import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SessionManagerService } from './session/session-manager.service';
import { CookieManagerService } from './session/cookie-manager.service';
import { MATH_KEY_SPOKEN_NAMES } from '../constant/math-key-spoken-names';

declare const window: any;

// ── Known-good voice patterns (Microsoft, Google, Apple) ──
const GOOD_VOICE_PATTERNS: RegExp[] = [
  // Microsoft / Windows voices
  /microsoft\s+jenny/i,
  /microsoft\s+aria/i,
  /microsoft\s+guy/i,
  /microsoft\s+david/i,
  /microsoft\s+zira/i,
  /microsoft\s+hazel/i,
  /microsoft\s+susan/i,
  /microsoft\s+george/i,
  /microsoft\s+catherine/i,
  /microsoft\s+linda/i,
  /microsoft\s+ravi/i,
  /microsoft\s+heera/i,

  // Google browser voices
  /google\s+us\s+english/i,
  /google\s+uk\s+english/i,
  /google\s+english/i,

  // Apple voices
  /samantha/i,
  /ava/i,
  /allison/i,
  /tom/i,
  /susan/i,
  /daniel/i,
  /serena/i,
  /karen/i,
  /tessa/i,
  /moira/i,
  /alex/i,
  /victoria/i,
];

// ── Known-bad / novelty voice patterns (iPad junk voices) ──
const BAD_VOICE_PATTERNS: RegExp[] = [
  /albert/i,
  /eddy/i,
  /flo/i,
  /grandma/i,
  /grandpa/i,
  /reed/i,
  /rocko/i,
  /rocco/i,
  /sandy/i,
  /shelley/i,
  /shelly/i,
  /bells/i,
  /boing/i,
  /bubbles/i,
  /cellos/i,
  /good\s?news/i,
  /bad\s?news/i,
  /jester/i,
  /organ/i,
  /trinoids/i,
  /whisper/i,
  /zarvox/i,
  /bahh/i,
  /hysterical/i,
  /pipe organ/i,
  /superstar/i,
  /wobble/i,
  /deranged/i,
  /eloquence/i,
];

/** Preview phrase used by the Preview button in the accessibility preferences. */
const TTS_PREVIEW_PHRASE = 'Two plus three equals five';

@Injectable({
  providedIn: 'root',
})
export class TtsService {
  private synth: SpeechSynthesis | null = null;
  private voice: SpeechSynthesisVoice | null = null;
  private readonly VOICE_COOKIE_KEY = 'mathKeyVoice';

  constructor(
    private readonly sessionManagerService: SessionManagerService,
    private readonly translateService: TranslateService,
    private readonly cookieManager: CookieManagerService,
  ) {
    if (this.isSupported()) {
      this.synth = window.speechSynthesis as SpeechSynthesis;
      this.loadVoices();

      if ('onvoiceschanged' in this.synth) {
        this.synth.onvoiceschanged = () => this.loadVoices();
      }
    }
  }

  /**
   * Main entry point: speak the given math key if TTS is enabled
   * and a spoken name is defined for the symbol.
   */
  speak(symbolKey: string): void {
    if (!this.isSupported() || !this.synth) return;

    const preferences = this.sessionManagerService.getUserPreferences();
    if (!preferences || !preferences.enableMathKeyTTS) return;

    const text = MATH_KEY_SPOKEN_NAMES[symbolKey];
    if (!text || !text.trim()) return;

    // Cancel any ongoing speech for snappy feedback
    this.synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const storedVoice = typeof preferences.ttsVoice === 'string' ? preferences.ttsVoice.trim() : '';
    const storedLang = typeof preferences.ttsVoiceLang === 'string' ? preferences.ttsVoiceLang.trim() : '';
    const allVoices = this.getAllVoices();
    let preferredVoice: SpeechSynthesisVoice | null = null;

    if (storedVoice) {
      // We persist the voice *name* in preferences; fall back to voiceURI just in case
      preferredVoice = allVoices.find(v => v.name === storedVoice) || allVoices.find(v => v.voiceURI === storedVoice) || null;
    }
    if (!preferredVoice && storedLang) {
      const langLower = storedLang.toLowerCase();
      preferredVoice =
        allVoices.find(v => (v.lang || '').toLowerCase() === langLower) ||
        allVoices.find(v => (v.lang || '').toLowerCase().startsWith(langLower.split('-')[0])) ||
        null;
    }

    // If stored voice is unavailable, fall back to system default voice
    if (!preferredVoice) {
      const systemDefault = allVoices.find(v => v.default);
      preferredVoice = systemDefault || this.voice;
    }

    const voiceToUse = preferredVoice || this.voice;
    if (voiceToUse) utterance.voice = voiceToUse;

    // Remember last used voice in a cookie for UI dropdown caching
    if (voiceToUse) {
      this.rememberVoice(voiceToUse);
    }

    // Reasonable defaults for kids
    const rate = typeof preferences.ttsRate === 'number' ? preferences.ttsRate : 1;
    utterance.rate = Math.min(2, Math.max(0.1, rate));
    utterance.pitch = 1;
    utterance.volume = 1;

    this.synth.speak(utterance);
  }

  /**
   * Speak a preview phrase using the given voice name (or system default).
   * Called from the Preview button in accessibility preferences.
   */
  speakPreview(voiceName: string | null, rate?: number): void {
    if (!this.isSupported() || !this.synth) return;

    this.synth.cancel();

    const utterance = new SpeechSynthesisUtterance(TTS_PREVIEW_PHRASE);
    const allVoices = this.getAllVoices();

    let voiceToUse: SpeechSynthesisVoice | null = null;
    if (voiceName) {
      voiceToUse = allVoices.find(v => v.name === voiceName) || allVoices.find(v => v.voiceURI === voiceName) || null;
    }
    if (!voiceToUse) {
      voiceToUse = allVoices.find(v => v.default) || this.voice || null;
    }

    if (voiceToUse) utterance.voice = voiceToUse;

    const effectiveRate = typeof rate === 'number' ? rate : 1;
    utterance.rate = Math.min(2, Math.max(0.1, effectiveRate));
    utterance.pitch = 1;
    utterance.volume = 1;

    this.synth.speak(utterance);
  }

  /**
   * Returns voices filtered and ranked for the current app language,
   * using a scoring algorithm that excludes known bad/novelty voices
   * and boosts known good ones. Intended for UI (preferences).
   */
  getAvailableVoices(): SpeechSynthesisVoice[] {
    const voices = this.getAllVoices();
    if (!voices.length) return [];

    const desiredLang = this.getDeviceLocale(); // full BCP-47, e.g. 'en-us', 'en-gb'

    // Score every voice and keep only those with a positive score
    const scored = voices
      .map(voice => ({
        voice,
        score: this.scoreVoice(voice, desiredLang),
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score);

    // If nothing passed the filter, show only the system default (if any)
    if (!scored.length) {
      const globalDefault = voices.find(v => v.default) || null;
      return globalDefault ? [globalDefault] : [];
    }

    // Cap to first 5 voices
    let limited = scored.slice(0, 5).map(item => item.voice);

    // Ensure the user's saved/cached voice is included even if it fell outside top 5
    const prefs = this.sessionManagerService.getUserPreferences();
    const storedPref = prefs && typeof prefs.ttsVoice === 'string' ? prefs.ttsVoice.trim() : '';
    const storedCookie = this.getRememberedVoiceKey();
    const storedKey = storedPref || storedCookie || '';

    if (storedKey) {
      const extra = voices.find(v => v.name === storedKey) || voices.find(v => v.voiceURI === storedKey) || null;
      if (extra && !limited.some(v => v.voiceURI === extra.voiceURI)) {
        // Only include if it's not a bad voice
        const extraScore = this.scoreVoice(extra, desiredLang);
        if (extraScore > 0) {
          limited = [extra, ...limited];
        }
      }
    }

    return limited;
  }

  /**
   * Score a voice for suitability. Higher is better.
   * Returns -999 for voices that should be excluded entirely.
   */
  private scoreVoice(voice: SpeechSynthesisVoice, desiredLang: string): number {
    const name = voice.name || '';
    const lang = voice.lang || '';

    const desiredBaseLang = desiredLang.split('-')[0].toLowerCase();
    const voiceLang = lang.toLowerCase();
    const voiceBaseLang = voiceLang.split('-')[0];

    // Wrong base language: exclude entirely
    if (voiceBaseLang !== desiredBaseLang) {
      return -999;
    }

    // Known unsuitable/novelty voices: exclude
    if (BAD_VOICE_PATTERNS.some(pattern => pattern.test(name))) {
      return -999;
    }

    let score = 0;

    // 3-tier region scoring:
    // Tier 1 — exact region match (e.g. voice 'en-us' === device 'en-us')
    if (voiceLang === desiredLang) {
      score += 200;
    } else {
      // Tier 2 — same base language, different region (e.g. 'en-gb' when device is 'en-us')
      score += 60;
    }

    // Known good names (Apple, Google, Microsoft premium voices)
    if (GOOD_VOICE_PATTERNS.some(pattern => pattern.test(name))) {
      score += 50;
    }

    // Browser/system default is usually the most natural voice for this device
    if (voice.default) {
      score += 150;
    }

    // Local voices are more reliable offline
    if (voice.localService) {
      score += 10;
    }

    return score;
  }

  private isSupported(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  private loadVoices(): void {
    if (!this.synth) return;

    const voices = this.getAllVoices();
    if (!voices.length) return;

    // Prefer English default voice as global fallback for math keys
    this.voice =
      voices.find(v => v.default && v.lang && v.lang.toLowerCase().startsWith('en')) ||
      voices.find(v => v.lang && v.lang.toLowerCase().startsWith('en')) ||
      voices[0];
  }

  /**
   * Raw voices from the underlying SpeechSynthesis engine.
   * Used internally for selection and compatibility.
   */
  private getAllVoices(): SpeechSynthesisVoice[] {
    if (!this.isSupported() || !this.synth) return [];
    return this.synth.getVoices() || [];
  }

  private rememberVoice(voice: SpeechSynthesisVoice): void {
    const key = voice.voiceURI || voice.name || '';
    if (!key) return;
    this.cookieManager.setCookie(this.VOICE_COOKIE_KEY, key);
  }

  private getRememberedVoiceKey(): string | null {
    try {
      const raw = this.cookieManager.getCookie(this.VOICE_COOKIE_KEY);
      const trimmed = raw && raw.trim();
      return trimmed || null;
    } catch {
      return null;
    }
  }

  /**
   * Determine current app language (en/es/fr) from TranslateService.
   */
  private getAppLang(): 'en' | 'es' | 'fr' {
    const lang = (this.translateService.currentLang || this.translateService.getDefaultLang() || 'en').toLowerCase();
    if (lang.startsWith('es')) return 'es';
    if (lang.startsWith('fr')) return 'fr';
    return 'en';
  }

  private getDeviceLocale(): string {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language.toLowerCase(); // e.g. 'en-us', 'en-gb', 'en-ie'
    }
    return this.getAppLang(); // SSR fallback: 'en' | 'es' | 'fr'
  }
}
